import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { StatusBadge } from '@/components/StatusBadge';
import { ClientDataTable } from '@/components/table/DataTable';
import { accountRoute, formatAccountType, getPersonOrgName, useHouseholdRoutePaths, useRequiredParams } from 'utils';
import { usePersonOrgProfilesForHousehold } from 'views/person/person-queries';
import {
    AccountGroupProfileWithAumIncluded,
    AccountGroupWithAumIncluded,
    AccountWithAumIncluded,
} from 'views/portfolio/account-aum-queries';
import { useDeleteAccount } from 'views/portfolio/account-queries';
import { DeleteValidationModal } from 'widgets/delete/DeleteValidationModal';

import { AccountBalance } from './AccountBalance';
import { AccountOwner } from './AccountOwner';
import { AccountRowMenu } from './AccountRowMenu';
import { LinkAccount } from './LinkAccount';
import { MoveAccountModal } from './MoveAccountModal';

interface FlatAccount extends AccountWithAumIncluded {
    accountGroup: AccountGroupWithAumIncluded;
}

interface FlatAccountsTableProps {
    accountGroupProfiles: AccountGroupProfileWithAumIncluded[];
}

const STATUS_ORDER = { REAL: 1, DRAFT: 2, ONBOARDING: 3, CLOSED: 4 } as const;

const AccountNameCell = ({ account }: { account: FlatAccount }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();

    return (
        <div>
            <Link to={accountRoute(account.accountGUID)} className="text-sm text-gray-700">
                {account.accountName.plainText}
            </Link>
            <div className="text-xs text-gray-500">
                {formatAccountType(account.accountType)}
                {account.accountGroup.accountGroupName?.plainText && (
                    <>
                        {' · '}
                        <Link
                            to={PortfolioPageRoutePath(globalAccountGUID, account.accountGroupGUID)}
                            className="text-primary cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid"
                        >
                            {account.accountGroup.accountGroupName.plainText}
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};

/** All accounts of a household in one table; the portfolio (account group) is shown under the account name. */
export const FlatAccountsTable = ({ accountGroupProfiles }: FlatAccountsTableProps) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutateAsync: deleteAccount, isPending: isDeleteAccountPending } = useDeleteAccount(globalAccountGUID);
    const [accountToDelete, setAccountToDelete] = useState<Account | undefined>();
    const [accountToMove, setAccountToMove] = useState<Account | undefined>();
    const { data: personOrgProfiles = [] } = usePersonOrgProfilesForHousehold(globalAccountGUID);

    const ownerNameMap = useMemo(() => {
        const map = new Map<string, string>();
        personOrgProfiles.forEach((profile) => {
            map.set(profile.personOrgGUID, getPersonOrgName(profile.personOrg));
        });
        return map;
    }, [personOrgProfiles]);

    const flatAccounts = useMemo<FlatAccount[]>(
        () =>
            accountGroupProfiles.flatMap((profile) =>
                profile.accounts.map((account) => ({ ...account, accountGroup: profile.accountGroup })),
            ),
        [accountGroupProfiles],
    );

    const columns = useMemo<ColumnDef<FlatAccount>[]>(
        () => [
            {
                id: 'name',
                header: 'ACCOUNT NAME',
                accessorFn: (account) => account.accountName.plainText.trim(),
                sortingFn: (a, b, columnId) => a.getValue<string>(columnId).localeCompare(b.getValue<string>(columnId)),
                minSize: 150,
                cell: ({ row }) => <AccountNameCell account={row.original} />,
            },
            {
                id: 'status',
                header: 'STATUS',
                accessorFn: (account) => STATUS_ORDER[account.lifecycleStatus] || 999,
                minSize: 80,
                cell: ({ row: { original: account } }) => (
                    <StatusBadge
                        status={account.lifecycleStatus}
                        liveDate={account.liveDate}
                        closedAt={account.closedAt}
                    />
                ),
            },
            {
                id: 'custody',
                header: 'CUSTODY',
                accessorFn: (account) => account.custodyAccountId || '',
                sortingFn: (a, b) =>
                    (a.original.custodyAccountId || '').localeCompare(b.original.custodyAccountId || ''),
                minSize: 120,
                cell: ({ row }) => <LinkAccount account={row.original} />,
            },
            {
                id: 'aum',
                header: 'AUM',
                accessorFn: (account) => parseFloat(account.aum || '0'),
                minSize: 100,
                cell: ({ row }) => <AccountBalance account={row.original} />,
            },
            {
                id: 'owner',
                header: 'ACCOUNT OWNER',
                accessorFn: (account) =>
                    account.ownerPersonOrgGUID ? ownerNameMap.get(account.ownerPersonOrgGUID) || '' : '',
                sortingFn: (a, b, columnId) => a.getValue<string>(columnId).localeCompare(b.getValue<string>(columnId)),
                minSize: 120,
                cell: ({ row }) => <AccountOwner account={row.original} />,
            },
            {
                id: 'actions',
                header: '',
                enableSorting: false,
                enableColumnFilter: false,
                enableResizing: false,
                size: 32,
                minSize: 32,
                cell: ({ row: { original: account } }) =>
                    account.lifecycleStatus !== LifecycleStatus.CLOSED ? (
                        <AccountRowMenu
                            account={account}
                            onDelete={setAccountToDelete}
                            onMove={setAccountToMove}
                            showGoToPortfolioAction
                        />
                    ) : null,
            },
        ],
        [ownerNameMap],
    );

    return (
        <>
            <ClientDataTable
                data={flatAccounts}
                columns={columns}
                getRowId={(account) => account.accountGUID}
                sortDescFirst={false}
                initialState={{ sorting: [{ id: 'aum', desc: true }] }}
                emptyMessage="No accounts found"
                className="overflow-x-auto rounded-lg border bg-white"
            />
            {accountToMove && (
                <MoveAccountModal
                    householdGUID={globalAccountGUID}
                    account={accountToMove}
                    accountGroups={accountGroupProfiles.map((profile) => profile.accountGroup)}
                    onClose={() => setAccountToMove(undefined)}
                />
            )}
            {accountToDelete && (
                <DeleteValidationModal
                    recordName={accountToDelete.accountName.plainText}
                    disabled={isDeleteAccountPending}
                    onDelete={() => deleteAccount(accountToDelete)}
                    onClose={() => setAccountToDelete(undefined)}
                />
            )}
        </>
    );
};
